import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as Lockr from 'lockr';
import { t } from 'i18next';

import { AppConfigurationStoreState, AppConfigurationStore } from './stores/AppConfigurationStore';
import { AppHelper } from './utils/AppHelper';
import { GlobalHelper } from './utils/GlobalHelper';
import { AjaxErrorHandler } from './utils/AjaxErrorHandler';
import { Page } from './components/mainContainer/Page';
import { PersistentStore } from './stores/PersistentStore';
import { ProjectSettingsStore } from './stores/ProjectSettingsStore';
import { SceneActions } from './actions/SceneActions';
import { CameraHelper } from './utils/CameraHelper';
import { ViewSettingsStore } from './stores/ViewSettingsStore';
import { LayoutType } from './constants/LayoutConstants';
import { PropertyEditorActions } from './actions/PropertyEditorActions';
import { ToolsActions } from './actions/ToolsActions';
import { ViewContainer } from './components/ViewContainer';
import { ArticleActions } from './actions/ArticleActions';
import { ViewSettingsActions } from './actions/ViewSettingsActions';
import { InteractionQueue } from './utils/InteractionQueue';
import { IOSScrollFix } from './utils/IOSScrollFix';
import { UIHelper } from './utils/UIHelper';
import { VisibilityActions } from './actions/VisibilityActions';
import { AppProjectManager } from './utils/AppProjectManager';
import { ProjectManagerActions } from './actions/ProjectManagerActions';
import { Overlay, SaveType } from './constants/Enums';
import { DownloadHelper } from './utils/DownloadHelper';
import { Stats } from './utils/Stats';
import { PconLoginActions } from './actions/PconLoginActions';
import { Hooks } from './utils/Hooks';
import { getImageSize, ImageSize } from './submodules/utils/Image';

// Submodules
import { getEnvVar } from './submodules/utils/ReactScriptHelper';

import pl = egr.wcf.pl;
import core = egr.wcf.core;
import utils = egr.wcf.utils;
import { UrlArgumentsStore } from './stores/UrlArgumentsStore';
import { enableSelfTalk } from './submodules/app-api/Communication';
import { registerHandler } from './submodules/app-api/MessageManager';
import { backend } from './submodules/app-api/Filesystem/DebugBackend';
import { registerWebAction } from './submodules/app-api/WebAction';
import { finishInitialization, startInitialization } from './submodules/app-api/Initialization';
import { methodWithDebugTimer } from './submodules/utils/Debug';
import { CustomLoginActions } from './actions/CustomLoginActions';

export const RELEASE_VERSION: string = getEnvVar('BUILD_VERSION', '');
export const RELEASE_COMMIT: string = getEnvVar('BUILD_COMMIT', '');
export const PRODUCT_NAME: string = getEnvVar('NAME', 'pCon.box');

export class Application {
    protected resizeTimeout: number | undefined;

    public constructor() {
        // Note: the following check is only used to ensure that the `Hooks` file
        // will be included in the bundle
        if (Hooks.preAppInstantiationHook === undefined) {
            console.error('Missing `preAppInstantiationHook`');
        }

        if (UrlArgumentsStore.getState()['debug-pv'] != null) {
            enableSelfTalk();
            registerHandler({
                type: 'fs',
                callback: backend
            });
            registerHandler({
                type: 'project',
                callback: (event: MessageEvent): void => {
                    const data: any = event.data;
                    if (data.type === 'project') {
                        let id: string;
                        let record: any;
                        const backendData: {[id: string]: any} = Lockr.get('pv') || {};
                        switch (data.action) {
                            case 'update':
                                id = data.arguments.id.toString();
                                if (id == null) {
                                    id = Date.now().toString();
                                }
                                record = backendData[id];
                                if (record == null) {
                                    record = {};
                                }

                                backendData[id] = jQuery.extend({}, record, data.arguments);

                                AppHelper.sendReply(data.transaction_id, false);
                                break;
                            case 'list':
                                AppHelper.sendReply(
                                    data.transaction_id,
                                    false,
                                    Object.keys(backendData).map((value: any): any => {
                                        if (data.arguments == null || data.arguments.fields == null) {
                                            return backendData[value];
                                        }

                                        const result: any = {};
                                        data.arguments.fields.forEach((name: string): void => {
                                            result[name] = backendData[value][name];
                                        });
                                        return result;
                                    })
                                );
                                break;
                            case 'delete':
                                data.arguments.project_id_list.forEach((value: any): void => {
                                    delete backendData[value];
                                });
                                AppHelper.sendReply(data.transaction_id, false);
                                break;
                            case 'get':
                                id = data.arguments.id.toString();
                                record = backendData[id];
                                if (record != null) {
                                    if (data.arguments.fields != null) {
                                        const newData: {} = {};
                                        data.arguments.fields.forEach((name: string): void => {
                                            newData[name] = record[name];
                                        });
                                        record = newData;
                                    }
                                    AppHelper.sendReply(data.transaction_id, false, record);
                                } else {
                                    AppHelper.sendReply(data.transaction_id, true);
                                }
                                break;
                            case 'set_content_url':
                                Lockr.set('pv:set_content_url', data.arguments);
                                AppHelper.sendReply(data.transaction_id, false);
                                break;
                            default:
                                break;
                        }
                        Lockr.set('pv', backendData);
                    }
                }
            });
        }

        registerWebAction(
            'synchronize',
            (event: MessageEvent): void => {
                if (event.data.arguments === 'foreground') { // load from obx if necessary
                    AppProjectManager.restoreCurrentSession();
                } else { // background: save obx
                    GlobalHelper.articleManager.synchronizeSession(true);
                }
            }
        );

        registerWebAction(
            'userinteraction',
            (event: MessageEvent): void => {
                if (event.data.arguments === 'back_pressed') {
                    ViewSettingsActions.changeHamburger(null);
                }
            }
        );

        registerWebAction(
            'openfile',
            (event: MessageEvent): void => {
                AppHelper.openFile(event);
            }
        );

        const appConfig: AppConfigurationStoreState = AppConfigurationStore.getState();

        if (appConfig.inApp) {
            startInitialization();
        } else {
            window.addEventListener('beforeunload', (e: BeforeUnloadEvent): string | null => {
                if (GlobalHelper.getArticleCount() > 0) {
                    const confirmationMessage: string = t('Your drawing will not be saved.');
                    e.returnValue = confirmationMessage;     // Gecko, Trident, Chrome 34+
                    return confirmationMessage;              // Gecko, WebKit, Chrome <34
                }
                return null;
            });
        }

        // connect global ajax error handler
        egr.wcf.utils.async.ajaxErrorHandler = AjaxErrorHandler.handler;

        ReactDOM.render(React.createElement(Page), document.getElementById('page-container'));

        if (!appConfig.noWebGL) {
            // create w-cf application
            GlobalHelper.initialize('viewer-container');

            GlobalHelper.setLanguage(PersistentStore.getState().language);
            GlobalHelper.coreApp.i18n.setLengthUnit(ProjectSettingsStore.getLengthUnit());
            GlobalHelper.coreApp.viewer.eventResize.addListener(SceneActions.setViewerDimension);
            SceneActions.setViewerDimension();

            const defaultCamera: core.view.CameraMode = (
                appConfig.architectureAvailable ?
                    core.view.CameraMode.Top :
                    core.view.CameraMode.Perspective
            );

            // create views
            CameraHelper.createView(new BABYLON.Viewport(0, 0, 1, 1), defaultCamera);
            SceneActions.setActiveView();

            // enable undo recording
            GlobalHelper.coreApp.commands.recordingEnabled = true;

            // set and start default tool
            GlobalHelper.coreApp.tools.defaultTool = new pl.tool.Default(GlobalHelper.coreApp);
            (GlobalHelper.coreApp.tools.defaultTool as pl.tool.Default).moveYBelowGroundEnabled = false;
            if (ViewSettingsStore.getState().layoutType === LayoutType.SMALL && AppConfigurationStore.getState().inApp) {
                (GlobalHelper.coreApp.tools.defaultTool as pl.tool.Default).rotateXEnabled = false;
                (GlobalHelper.coreApp.tools.defaultTool as pl.tool.Default).rotateZEnabled = false;
                /* may be changed on resize, see Globals.updateDefaultTool */
            }
            GlobalHelper.coreApp.tools.startDefaultTool();

            // eaiws
            GlobalHelper.coreApp.model.selectionProperties.eventPropertiesChanged.addListener((pChangeFlag) => {
                PropertyEditorActions.invalidateProperties();
            });
            GlobalHelper.coreApp.model.eventSelectionChanged.addListener(SceneActions.updateSelection);
            (GlobalHelper.coreApp.tools.defaultTool as pl.tool.Default).eventRectangleSelectFinished.addListener(ToolsActions.endRectangleSelect);

            GlobalHelper.createTestScene();

            setTimeout(() => { // ensure that .wcf-core.view was created
                jQuery('.wcf-core.view').each((index: number, element: Element): void => {
                    const wrapper: HTMLDivElement = document.createElement('div');
                    wrapper.className = 'viewcontainer';
                    element.appendChild(wrapper);
                    ReactDOM.render(React.createElement(ViewContainer, {view: element}), wrapper);
                });
                GlobalHelper.roomManager.setHiddenWalls(PersistentStore.getState().frontWalls === 'invisible');
            });

            // FixMe: remove jquery dom manipulation
            const setSidebarPosition: () => void = (): void => {
                // notify viewer that position/size has changed
                GlobalHelper.coreApp.viewer.resize();
            };
            PersistentStore.addListener(setSidebarPosition);
            setSidebarPosition();

            GlobalHelper.articleManager.eventArticleChanged.addListener(() => {
                ArticleActions.invalidateArticleList();
            });

            GlobalHelper.coreApp.viewer.eventActiveViewChanged.addListener(SceneActions.setActiveView);
            GlobalHelper.coreApp.viewer.eventResize.addListener(SceneActions.setActiveView);

            // handle window resize
            let canvasBouncer: () => void;
            const resize: () => void = (): void => {
                this.resizeTimeout = undefined;
                ViewSettingsActions.triggerResize();
                setTimeout(canvasBouncer);
            };

            const startResize: () => void = (): void => {
                if (this.resizeTimeout != null) {
                    clearTimeout(this.resizeTimeout);
                }
                this.resizeTimeout = window.setTimeout(resize, 100);
            };

            const bouncerPasses: number = 20;
            const bouncerInterval: number = 150;

            let bouncerHandle: number | undefined;
            canvasBouncer = (): void => {
                // Note: this is just a ugly workaround for a race condition on iOS
                if (bouncerInterval != null) {
                    clearInterval(bouncerHandle);
                }

                let pass: number = 0;
                const width: number = jQuery('body').width();
                const height: number = jQuery('body').height();

                bouncerHandle = window.setInterval(
                    (): void => {
                        const needsResize: boolean = jQuery('body').width() !== width || jQuery('body').height() !== height;
                        if (needsResize) {
                            startResize();
                        }

                        if (needsResize || pass++ > bouncerPasses) {
                            clearInterval(bouncerHandle);
                        }
                    },
                    bouncerInterval
                );
            };

            window.addEventListener('resize', startResize, false);
            window.addEventListener(
                'orientationchange',
                () => {
                    startResize();
                },
                false
            );

            startResize();

            // Start a new interaction in order to delay actions related to the
            // app start (open a given file) until after the user choose between
            // a pCon.login based or a startup based session.
            InteractionQueue.add((): Promise<void> => {
                return this.initializeSession(appConfig);
            });

            if (!AppConfigurationStore.getState().inApp) {
                // Allows to import images and pec files via drag-and-drop or the clipboard
                const importFileList: (pFiles: any) => void = (pFiles: any): void => {
                    if (pFiles == null || !pFiles.length) {
                        return;
                    }
                    ViewSettingsActions.withLock<void>(async (): Promise<void> => {
                        for (const file of pFiles) {
                            if (/\.(png|jpe?g|gif|bmp)$/i.test(file.name)) {
                                const tResult: any = await new Promise<any>((resolve, reject) => {
                                    const reader: FileReader = new FileReader();
                                    reader.onerror = () => {
                                        reject();
                                    };
                                    reader.onload = async () => {
                                        resolve(reader.result);
                                    };
                                    reader.readAsDataURL(file);
                                });
                                const tImageSize: ImageSize | null = await getImageSize(tResult);
                                if (tImageSize) {
                                    await GlobalHelper.roomManager.insertBackgroundImage(tResult, tImageSize.width, tImageSize.height, 0, 0);
                                }
                            } else if (/\.pec$/i.test(file.name) && GlobalHelper.eaiwsSession.isValid) {
                                Stats.sendEvent({
                                    action: 'Plandat'
                                });
                                await SceneActions.importPecFile(file);
                            }
                        }
                    });
                };

                window.addEventListener(
                    'drop',
                    (event: any) => {
                        event.stopPropagation();
                        event.preventDefault();
                        importFileList(event.dataTransfer.files);
                    },
                    false
                );

                window.addEventListener(
                    'dragover',
                    (event: any) => {
                        event.preventDefault();
                    },
                    false
                );

                window.addEventListener(
                    'paste',
                    (event: any) => {
                        if (event.clipboardData != null) {
                            importFileList(event.clipboardData.files);
                        }
                    },
                    false
                );
            }
        }
        if (appConfig.iDevice) {
            new IOSScrollFix().enable();
        }
        ViewSettingsActions.updateTooltipPositions();
    }

    @methodWithDebugTimer('initializeSession')
    private async initializeSession(appConfig: AppConfigurationStoreState): Promise<void> {
        await UIHelper.ignoreReject(this.checkPconLoginState());

        await UIHelper.ignoreReject(CustomLoginActions.backgroundLogin());

        VisibilityActions.changeOverlay(Overlay.NONE);

        const projectTasks: Array<Promise<void>> = [];

        if (appConfig.projectManagerAvailable) {
            projectTasks.push(UIHelper.ignoreReject(AppProjectManager.migrateProjects()));
            projectTasks.push(UIHelper.ignoreReject(ProjectManagerActions.updateProjectList()));
            finishInitialization();
        }

        if (projectTasks.length) {
            await ViewSettingsActions.withLock(async (): Promise<void> => {
                await Promise.all(projectTasks);
            });
        }

        if (!PersistentStore.shouldStartWithProjectManagement()) {
            const autoSaveId: string | undefined = PersistentStore.getState().autoSaveId;
            await SceneActions.newScene(
                {
                    projectLoadingHook: async (): Promise<void> => {
                        const pboxUrl: string | null = utils.string.getUrlParamValue(window.location.href, 'pbox');
                        if (utils.string.isNotNullOrEmpty(pboxUrl)) {
                            Stats.sendEvent({
                                action: 'Planlink'
                            });
                            const buffer: ArrayBuffer = await DownloadHelper.getContent(pboxUrl);
                            const container: Blob = new Blob([buffer], {type: 'application/zip'});
                            await GlobalHelper.coreApp.document.loadFromZip(container);
                        } else if (appConfig.projectManagerAvailable) {
                            const couldRestoreSession: boolean = await AppProjectManager.restoreLastProject(autoSaveId);
                            if (!couldRestoreSession) {
                                await AppProjectManager.loadProject(null);
                            }
                        }
                    }
                },
                SaveType.NOT
            );
        }

        VisibilityActions.showPriceInfo(true);
    }

    // private //
    @methodWithDebugTimer('checkPconLoginState')
    private async checkPconLoginState(): Promise<void> {
        Stats.updateLoginState();
        if (!AppConfigurationStore.getState().noLogin) {
            await UIHelper.ignoreReject(PconLoginActions.backgroundLogin());
        }
    }
}

(window as any).egr.wcf.Application = Application;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/App.tsx